import React from "react";
import {useStores} from "../../../../../../stores";
import type {PartGridGroupModel, PartGridRowModel} from "../CategorizationTableDataModel.ts";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import {AuthorChip} from "../../../../../taxonomy-editor/approval/component/AuthorChip.tsx";
import {AcceptButtons} from "../../../approval-acc-rej/AcceptButtons.tsx";
import {Error as ErrorIcon, Refresh} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import {MatPartReviewRowState} from "../../../../../../services/classes/MaterializedClasses.ts";
import {isReviewRowAcceptable} from "../../../../../../services/classes/MatReviewHelpers.ts";
import IconButton from "@mui/material/IconButton";
import type {GridRowParams} from "@mui/x-data-grid-premium";

export const GRID_REVIEW_ACTION_FIELD = '__review_actions__';

type Props = GridRowParams<PartGridRowModel> & {}
export const ReviewActionsCell: React.FC<Props> = ({row}) => {
    const {categorizationReviewStore} = useStores();

    const onAccept = React.useCallback(() => {
        // When the action is performed, reset the selection to avoid confusion for the user
        const rpc = categorizationReviewStore.reviewPageController;
        rpc.reviewPageSelectionController.clearSelection();

        rpc.reviewPageTableController.onReviewCategory(row.id || String(row.part), null)
    }, [categorizationReviewStore, row.id]);
    // Note that group could be empty string
    if (row.group !== undefined) {
        const groupRow = row as unknown as PartGridGroupModel;
        if (groupRow.hotfixV8DisableInteractions || groupRow.isOutOfSync) {
            return <Grid container spacing={1} alignItems="center" justifyContent="space-evenly">
                <Grid>
                    <Tooltip title="Please refresh to see the latest changes">
                        <IconButton onClick={() => {
                            const rpc = categorizationReviewStore.reviewPageController;
                            rpc.reviewPageTableController.refreshGroupedRow(row.id);
                        }}>
                            <Refresh/>
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
        }
        return null;
    }
    if (!row.state) {
        // For dummy and footer rows
        return null;
    }
    const partRow = row as PartGridRowModel;

    let canEditAccept = false;
    let tooltipText: string | undefined;
    if (row.state === MatPartReviewRowState.pending) {
        tooltipText = "Cannot accept already pending changes"
    } else if (row.state === MatPartReviewRowState.approved) {
        tooltipText = "Cannot accept already approved changes"
    } else if (!categorizationReviewStore.canEditReviewPage) {
        tooltipText = "Review is disabled"
    } else if (!isReviewRowAcceptable(partRow)) {
        tooltipText = "Cannot accept changes"
    } else {
        tooltipText = undefined;
        canEditAccept = true;
    }

    return <Grid container alignItems="center" justifyContent="space-evenly">
        {row.isLoading
            ? <Grid>
                <CircularProgress size={26}/>
            </Grid>
            : <>
                <Grid>
                    <Tooltip title={tooltipText} arrow>
                        {row.state === MatPartReviewRowState.inconsistent
                            ? <ErrorIcon color="error"/>
                            : <span>
                                <AcceptButtons
                                    choice={row.review_choice}
                                    onClick={canEditAccept ? onAccept : undefined}/>
                            </span>
                        }
                    </Tooltip>
                </Grid>
                {row.review_user_id &&
                    <Grid>
                        <AuthorChip
                            authorId={row.review_user_id}
                            onlyIcon
                            tooltipTemplate="Reviewed by {fullName}"
                        />
                    </Grid>
                }
            </>}
    </Grid>
}
